import Gumshoe from 'gumshoejs';
import omit from "omit.js";
import React, { useEffect, useRef, useCallback, useMemo } from 'react';
import { WebUI } from "../../wui-core/src/index";
import { AnchorProps } from './iAnchor';

const defaultProps = {
    navClass: 'active',
    contentClass: 'active',
    nested: false,
    nestedClass: 'active',
    offset: 0,
    reflow: false,
    events: true,
    clsPrefix: 'u-anchor',
    activeHandle: () => {},
    deactiveHandle: () => {}
};

const Anchor: React.FC<AnchorProps> = (incomingProps) => {
    // ==================== Props ====================
    const props = {
        ...defaultProps,
        ...incomingProps
    };

    const { clsPrefix, ...others } = props;

    // ==================== Refs ====================
    const anchorRef = useRef<any>(null);
    const anchorDOMRef = useRef<HTMLDivElement>(null);

    // ==================== 事件处理 ====================
    const gumshoeActivateHandle = useCallback((event: any) => {
        const li = event.target;
        const link = event.detail.link;
        const content = event.detail.content;
        props.activeHandle?.(li, link, content);
    }, [props.activeHandle]);

    const gumshoeDeactivateHandle = useCallback((event: any) => {
        const li = event.target;
        const link = event.detail.link;
        const content = event.detail.content;
        props.deactiveHandle?.(li, link, content);
    }, [props.deactiveHandle]);

    // ==================== Gumshoe 配置 ====================
    const gumshoeOptions = useMemo(() => ({
        navClass: props.navClass,
        contentClass: props.contentClass,
        nested: props.nested,
        nestedClass: props.nestedClass,
        offset: props.offset,
        reflow: props.reflow,
        events: props.events
    }), [props.navClass, props.contentClass, props.nested, props.nestedClass, props.offset, props.reflow, props.events]);

    // ==================== 初始化和清理 ====================
    useEffect(() => {
        if (!anchorDOMRef.current || !props.selector) return;

        // 初始化 Gumshoe
        anchorRef.current = new Gumshoe(props.selector, gumshoeOptions);

        // 添加事件监听
        const anchorElement = anchorDOMRef.current;
        anchorElement.addEventListener('gumshoeActivate', gumshoeActivateHandle);
        anchorElement.addEventListener('gumshoeDeactivate', gumshoeDeactivateHandle);

        // 清理函数
        return () => {
            if (anchorRef.current) {
                anchorRef.current.destroy();
                anchorRef.current = null;
            }
            anchorElement.removeEventListener('gumshoeActivate', gumshoeActivateHandle);
            anchorElement.removeEventListener('gumshoeDeactivate', gumshoeDeactivateHandle);
        };
    }, [props.selector, gumshoeOptions, gumshoeActivateHandle, gumshoeDeactivateHandle]);

    // ==================== 更新检测 ====================
    useEffect(() => {
        if (anchorRef.current) {
            anchorRef.current.setup();
            anchorRef.current.detect();
        }
    }, [props.navClass, props.contentClass, props.nested, props.nestedClass, props.offset, props.reflow]);

    // ==================== 过滤 DOM Props ====================
    const domProps = useMemo(() => omit(others, [
        "navClass", "contentClass", "nested", "nestedClass",
        "reflow", "events", "activeHandle", "deactiveHandle", "selector"
    ]), [others]);

    // ==================== 渲染 ====================
    return (
        <div
            className={clsPrefix}
            ref={anchorDOMRef}
            {...domProps}
        >
            {props.children}
        </div>
    );
};

export default WebUI({ name: "anchor", defaultProps })(Anchor);
